import React, { useState, useRef, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import openSocket from "../../services/socket-io";
import useSound from "use-sound";
import { MessageSquare } from "lucide-react";

import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

import TicketListItem from "../TicketListItem";
import { i18n } from "../../translate/i18n";
import useTickets from "../../hooks/useTickets";
import useVolumeDoAlerta from "../../hooks/useVolumeDoAlerta";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";

const NotificationsPopOver = () => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { user } = useContext(AuthContext);
	const ticketIdUrl = +pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);

	const [, setDesktopNotifications] = useState([]);

	const { tickets } = useTickets({ withUnreadMessages: "true" });
	// O volume é o do controle único no topo (ao lado do perfil). Vai para o
	// hook do som, que o reaplica no Howler quando muda — sem recarregar.
	const { volume } = useVolumeDoAlerta();
	const [play] = useSound(alertSound, { volume });
	const soundAlertRef = useRef();

	const navigateRef = useRef(navigate);

	useEffect(() => {
		soundAlertRef.current = play;

		if (!("Notification" in window)) {
			console.warn("This browser doesn't support notifications");
		} else {
			Notification.requestPermission();
		}
	}, [play]);

	useEffect(() => {
		setNotifications(tickets);
	}, [tickets]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		const socket = openSocket();

		socket.on("connect", () => socket.emit("joinNotification"));

		socket.on("ticket", (data) => {
			if (data.action === "updateUnread" || data.action === "delete") {
				setNotifications((prevState) => {
					const ticketIndex = prevState.findIndex(
						(t) => t.id === data.ticketId
					);
					if (ticketIndex !== -1) {
						prevState.splice(ticketIndex, 1);
						return [...prevState];
					}
					return prevState;
				});

				setDesktopNotifications((prevState) => {
					const notfiticationIndex = prevState.findIndex(
						(n) => n.tag === String(data.ticketId)
					);
					if (notfiticationIndex !== -1) {
						prevState[notfiticationIndex].close();
						prevState.splice(notfiticationIndex, 1);
						return [...prevState];
					}
					return prevState;
				});
			}
		});

		socket.on("appMessage", (data) => {
			if (
				data.action === "create" &&
				!data.message.read &&
				(data.ticket.userId === user?.id || !data.ticket.userId)
			) {
				setNotifications((prevState) => {
					const ticketIndex = prevState.findIndex(
						(t) => t.id === data.ticket.id
					);
					if (ticketIndex !== -1) {
						prevState[ticketIndex] = data.ticket;
						return [...prevState];
					}
					return [data.ticket, ...prevState];
				});

				// Grupo também avisa.
				//
				// `isGroup` estava nesta lista: mensagem de grupo nunca tocava
				// som nem gerava aviso, e quem atendia só descobria a conversa
				// ao olhar a lista por acaso. O silêncio continua valendo para
				// o que já está à vista e para o que é de outro atendente.
				const shouldNotNotificate =
					(data.message.ticketId === ticketIdRef.current &&
						document.visibilityState === "visible") ||
					(data.ticket.userId && data.ticket.userId !== user?.id);

				if (shouldNotNotificate) return;

				handleNotifications(data);
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [user]);

	const handleNotifications = (data) => {
		const { message, contact, ticket } = data;

		const options = {
			body: `${message.body} - ${format(new Date(), "HH:mm")}`,
			icon: contact.profilePicUrl,
			tag: ticket.id,
			renotify: true,
		};

		const notification = new Notification(
			`${i18n.t("tickets.notification.message")} ${contact.name}`,
			options
		);

		notification.onclick = (e) => {
			e.preventDefault();
			window.focus();
			navigateRef.current(`/tickets/${ticket.id}`);
		};

		setDesktopNotifications((prevState) => {
			const notfiticationIndex = prevState.findIndex(
				(n) => n.tag === notification.tag
			);
			if (notfiticationIndex !== -1) {
				prevState[notfiticationIndex] = notification;
				return [...prevState];
			}
			return [notification, ...prevState];
		});

		soundAlertRef.current();
	};

	return (
		<Popover open={isOpen} onOpenChange={setIsOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="relative"
					aria-label="Open Notifications"
				>
					<MessageSquare className="h-5 w-5" />
					{notifications.length > 0 && (
						<span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold tabular-nums text-destructive-foreground">
							{notifications.length}
						</span>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent align="end" className="w-[min(22rem,calc(100vw-2rem))] p-0">
				<div className="border-b px-3 py-2">
					<p className="text-sm font-medium">Não lidas</p>
				</div>
				<div className="max-h-[350px] overflow-y-auto">
					{notifications.length === 0 ? (
						<p className="px-3 py-8 text-center text-xs text-muted-foreground">
							{i18n.t("notifications.noTickets")}
						</p>
					) : (
						notifications.map((ticket) => (
							// Clicar num item navega e fecha o popover.
							<div key={ticket.id} onClick={() => setIsOpen(false)}>
								<TicketListItem ticket={ticket} />
							</div>
						))
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
};

export default NotificationsPopOver;
